import { AreaChart, Hourglass, ShieldCheck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../contexts/TranslationContext';
import type { PlatformImpact } from '../services/platformImpact';
import { type CountUpPhase, useLoopingCountUp } from '../hooks/useLoopingCountUp';
import { usePlatformImpact } from '../hooks/usePlatformImpact';
import { LandingImpactCard } from './LandingImpactCard';
import { FadeSwitch } from './FadeSwitch';
import { LandingDataPlaceholder } from './LandingDataPlaceholder';
import { landingSectionClassName } from './landingSectionClassName';
import { AI_RELIABILITY_PATH, localizePublicHref } from '../utils/publicRoutes';

const HOURS_PER_WORK_DAY = 8;

function formatImpactNumber(value: number, locale: string, maximumFractionDigits = 1): string {
  if (!Number.isFinite(value) || value < 0) return '—';

  return new Intl.NumberFormat(locale, { maximumFractionDigits }).format(value);
}

function displayedAnalysesProgressValue(
  value: number,
  phase: CountUpPhase,
  analysesTarget: number | undefined,
  hoursTarget: number | undefined,
): number {
  if (
    analysesTarget === undefined
    || hoursTarget === undefined
    || !Number.isFinite(value)
  ) {
    return value;
  }

  if (phase !== 'counting' && phase !== 'idle') return Math.floor(value);

  const startValue = Math.floor(analysesTarget * 0.8);
  const targetValue = Math.floor(analysesTarget);
  const totalIncrease = Math.max(targetValue - startValue, 0);
  const visualStepCount = Math.max(Math.ceil(hoursTarget * 0.2), 1);
  const progress = value >= analysesTarget
    ? 1
    : Math.min(Math.max((value - (analysesTarget * 0.8)) / (analysesTarget * 0.2), 0), 1);
  const completedSteps = Math.min(Math.floor(progress * visualStepCount), visualStepCount);
  const organicStepPattern = [2, 4, 1, 4, 4];
  const allStepWeights = Array.from(
    { length: visualStepCount },
    (_, index) => organicStepPattern[index % organicStepPattern.length],
  );
  const completedWeight = allStepWeights
    .slice(0, completedSteps)
    .reduce((sum, stepWeight) => sum + stepWeight, 0);
  const totalWeight = allStepWeights.reduce((sum, stepWeight) => sum + stepWeight, 0);

  return startValue + Math.round(totalIncrease * (completedWeight / totalWeight));
}

interface LandingImpactSectionProps {
  initialImpact?: PlatformImpact;
}

export function LandingImpactSection({ initialImpact }: LandingImpactSectionProps = {}) {
  const { t, language } = useTranslation();
  const impactCardsRef = useRef<HTMLDivElement>(null);
  const analysesCadenceTargetRef = useRef<number>();
  const hoursCadenceTargetRef = useRef<number>();
  const [isSectionVisible, setIsSectionVisible] = useState(false);
  const numberLocale = language === 'fr' ? 'fr-FR' : 'en-US';
  const { data, isError, isLoading } = usePlatformImpact(initialImpact);
  const { displayedValue: displayedAnalyses, phase: analysesPhase } = useLoopingCountUp(
    data?.totalAnalyses,
    isSectionVisible,
  );
  if (analysesPhase !== 'counting' || analysesCadenceTargetRef.current === undefined) {
    analysesCadenceTargetRef.current = data?.totalAnalyses;
    hoursCadenceTargetRef.current = data?.estimatedSavedHours;
  }

  const displayedAnalysesValue = displayedAnalysesProgressValue(
    displayedAnalyses,
    analysesPhase,
    analysesCadenceTargetRef.current,
    hoursCadenceTargetRef.current,
  );
  const displayedSavedHours = displayedAnalysesValue / 2;
  const displayedSavedDays = Math.ceil(displayedSavedHours / HOURS_PER_WORK_DAY);

  useEffect(() => {
    const impactCards = impactCardsRef.current;
    if (!impactCards) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setIsSectionVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(([entry]) => {
      setIsSectionVisible(entry.isIntersecting);
    }, { threshold: 0.15 });

    observer.observe(impactCards);
    return () => observer.disconnect();
  }, [isLoading]);

  return (
    <section
      id="platform-impact"
      data-landing-scroll-target
      className={landingSectionClassName({
        tone: 'soft',
        className: 'px-4 py-4 sm:flex sm:min-h-[100svh] sm:flex-col sm:justify-center sm:px-6 sm:py-6',
        fillAvailable: false,
      })}
    >
      <div className="mx-auto w-full max-w-[96rem]">
        <FadeSwitch
          viewKey={isLoading ? 'loading' : 'ready'}
          className="relative min-h-[23rem] sm:min-h-[57rem] lg:min-h-[70svh]"
          childClassName="min-h-[23rem] sm:min-h-[57rem] lg:min-h-[70svh]"
        >
          {isLoading ? (
            <LandingDataPlaceholder
              className="min-h-[23rem] sm:min-h-[57rem] lg:min-h-[70svh]"
              label={t('impact.loading')}
              testId="platform-impact-loader"
            />
          ) : (
            <div
              ref={impactCardsRef}
              className="grid grid-cols-2 content-start items-stretch gap-3 sm:min-h-[57rem] sm:gap-5 lg:min-h-[70svh] lg:grid-cols-2 lg:grid-rows-2 lg:content-normal lg:gap-6"
              data-testid="platform-impact-cards"
            >
              <LandingImpactCard
                className="lg:col-start-1 lg:row-start-1"
                icon={AreaChart}
                label={t('impact.completedAnalyses')}
                note={t('impact.analysesMethodology')}
                phase={analysesPhase}
                tone="analyses"
                value={isError ? t('impact.unavailable') : formatImpactNumber(
                  displayedAnalysesValue,
                  numberLocale,
                  0,
                )}
                valueTestId="impact-analyses-value"
              />
              <LandingImpactCard
                className="lg:col-start-1 lg:row-start-2"
                detail={isError ? null : t('impact.savedDays').replace('{days}', formatImpactNumber(displayedSavedDays, numberLocale, 0))}
                icon={Hourglass}
                label={t('impact.savedHours')}
                note={t('impact.methodology')}
                phase={analysesPhase}
                tone="hours"
                value={isError ? t('impact.unavailable') : formatImpactNumber(
                  displayedSavedHours,
                  numberLocale,
                )}
                valueTestId="impact-hours-value"
              />
              <LandingImpactCard
                className="col-span-2 mx-auto w-3/4 sm:w-[calc(50%-0.625rem)] lg:col-span-1 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:w-auto"
                emphasizeAsComplete={isSectionVisible}
                href={localizePublicHref(AI_RELIABILITY_PATH, language)}
                icon={ShieldCheck}
                label={t('impact.reliability')}
                linkLabel={t('impact.reliabilityLink')}
                note={t('impact.reliabilityMethodology')}
                phase={analysesPhase}
                tone="reliability"
                value={t('impact.reliabilityValue')}
                valueTestId="impact-reliability-value"
              />
            </div>
          )}
        </FadeSwitch>
      </div>
    </section>
  );
}
